import React, { FC, RefObject } from 'react';
import { Placement } from '@popperjs/core';
import { useTranslation } from 'react-i18next';
import { Popup, PopupTheme } from '../../../shared/components/Popup/Popup';
import { mapLoanFormat } from '../../../../models/LoanFormat';
import { LoanFormatState } from '../../../app-store/loanFormat/loanFormatReducer';

type Props = {
    readonly loanFormat: LoanFormatState;
    readonly ownStockQuantity?: number;
    readonly foreignStockQuantity?: number;
    readonly isInPartnerContext?: boolean;
};

type ReserveListCopyStockDropdownProps = {
    readonly placement: Placement;
    readonly visible: boolean;
    readonly popoverRef: RefObject<HTMLElement>;
    readonly loanFormat: LoanFormatState;
    readonly ownStockQuantity?: number;
    readonly foreignStockQuantity?: number;
    readonly isInPartnerContext?: boolean;
};
export const ReserveListCopyStockDropdown: FC<ReserveListCopyStockDropdownProps> = ({
    placement,
    visible,
    popoverRef,
    loanFormat,
    ownStockQuantity,
    foreignStockQuantity,
    isInPartnerContext
}) => {
    return (
        <Popup
            target={popoverRef}
            visible={visible}
            placement={placement}
            displacement={-24}
            theme={PopupTheme.CUSTOM}
            popupClassName="connect-popover"
        >
            <ReserveListStockInfoList
                loanFormat={loanFormat}
                ownStockQuantity={ownStockQuantity}
                foreignStockQuantity={foreignStockQuantity}
                isInPartnerContext={isInPartnerContext}
            />
        </Popup>
    );
};

const ReserveListStockInfoList: FC<Props> = ({
    loanFormat,
    ownStockQuantity = 0,
    foreignStockQuantity = 0,
    isInPartnerContext = false
}) => {
    const { t } = useTranslation();

    if (ownStockQuantity === 0 && foreignStockQuantity === 0) {
        return null;
    }

    const ownStockQuantityKey = isInPartnerContext ? 'sharedCollection' : 'individualCollection';
    const foreignStockQuantityKey = isInPartnerContext ? 'individualCollection' : 'sharedCollection';

    return (
        <>
            {ownStockQuantity ? (
                <section className="bordered-section" key={ownStockQuantityKey}>
                    <span className="title">{t(ownStockQuantityKey)}</span>
                    <table className="left-aligned-data" data-testid="stock-info-dropdown">
                        <tbody>
                            <tr className="emphasis">
                                <td>
                                    <span>{ownStockQuantity}</span>
                                </td>
                                <td>
                                    <b>
                                        {t(`loanFormat.plain.${mapLoanFormat(loanFormat.requestKey)?.toLowerCase()}`, {
                                            count: ownStockQuantity
                                        })}{' '}
                                        - {t('lendingModel.ocou')}
                                    </b>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </section>
            ) : null}
            {foreignStockQuantity ? (
                <section className="bordered-section" key={foreignStockQuantityKey}>
                    <span className="title">{t(foreignStockQuantityKey)}</span>
                    <table className="left-aligned-data" data-testid="stock-info-dropdown">
                        <tbody>
                            <tr className="emphasis">
                                <td>
                                    <span>{foreignStockQuantity}</span>
                                </td>
                                <td>
                                    <b>
                                        {t(`loanFormat.plain.${mapLoanFormat(loanFormat.requestKey)?.toLowerCase()}`, {
                                            count: foreignStockQuantity
                                        })}{' '}
                                        - {t('lendingModel.ocou')}
                                    </b>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </section>
            ) : null}
        </>
    );
};
